/**
 * Send To Section
 *
 * Edit mode UI for configuring WHO receives the automation notification.
 * This is different from the "Who" section which filters which clients trigger the automation.
 *
 * Send To determines the recipient:
 * - Send to Client (default when sendToIds is empty)
 * - Send to specific staff members
 * - Send to Assigned Trainer (sendToIds contains 0)
 *
 * V2 Reference: v2/app/components/dashboard/trainer/reminder-send-to.js
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchUserQueryKey, fetchUsers } from 'src/models/user/user';
import { getUserDisplayName } from 'src/models/user/utils';

import { AutomationItem } from '../types';

interface SendToSectionProps {
  automation: AutomationItem;
  index: number;
  /** Placeholder text when sending to client (default: "Send to Client") */
  placeholder?: string;
}

interface SendToOption {
  value: number;
  label: string;
}

/**
 * Send To Section - Edit mode
 */
export const SendToSection: FC<SendToSectionProps> = ({
  automation,
  index,
  placeholder,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch trainers
  const { data: trainers = [], isLoading } = useQuery({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  // Build options list - convert id to number for consistent comparison
  const options: SendToOption[] = trainers.map((trainer) => ({
    value: trainer.id as number,
    label: getUserDisplayName(trainer),
  }));

  // Add special option for "Assigned Trainer" (value: 0)
  const specialLabel = getSpecialOptionLabel(automation, t);
  options.unshift({
    value: 0,
    label: specialLabel,
  });

  // Convert sendToIds to numbers for consistent comparison
  const sendToIds = (automation.sendToIds || []).map((id) =>
    typeof id === 'string' ? parseInt(id, 10) : id
  );

  // Track if user has explicitly unchecked "Send to Client"
  // Initial state: checked if sendToIds is empty (default behavior)
  const [sendToClientChecked, setSendToClientChecked] = useState(sendToIds.length === 0);

  // Get selected options
  const selectedOptions = options.filter((opt) => sendToIds.includes(opt.value));

  const handleToggleSendToClient = (checked: boolean) => {
    setSendToClientChecked(checked);
    if (checked) {
      // Clear sendToIds to send to client
      setFieldValue(`automations.${index}.sendToIds`, []);
    }
    // When unchecking, don't pre-populate - just show empty dropdown
  };

  const handleSendToIdsChange = (newValues: SendToOption[]) => {
    const ids = newValues.map((opt) => opt.value);
    setFieldValue(`automations.${index}.sendToIds`, ids);
  };

  const placeholderText = placeholder || t('automations.sendTo.sendToClient');

  return (
    <Stack spacing={2}>
      {/* Send to Client checkbox */}
      <Box>
        <Checkbox
          label={placeholderText}
          checked={sendToClientChecked}
          onChange={(e) => handleToggleSendToClient(e.target.checked)}
        />
      </Box>

      {/* Staff selection (only shown when checkbox is unchecked) */}
      {!sendToClientChecked && (
        <Box sx={{ maxWidth: 400 }}>
          <Autocomplete
            multiple
            options={options}
            value={selectedOptions}
            onChange={(_, newValue) => handleSendToIdsChange(newValue as SendToOption[])}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            loading={isLoading}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedOptions.length === 0 ? t('automations.sendTo.selectStaff') : ''}
                size="small"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option.value} {...rest}>
                  {option.label}
                </li>
              );
            }}
            size="small"
          />
        </Box>
      )}
    </Stack>
  );
};

/**
 * Get the special option label based on automation type
 */
function getSpecialOptionLabel(
  automation: AutomationItem,
  t: (key: string) => string
): string {
  if (automation.customType === 'upcoming-appointment') {
    return t('automations.sendTo.sendToSessionTrainer');
  }
  if (automation.reminderType === 'low-stock-threshold') {
    return t('automations.sendTo.sendToLocationStaff');
  }
  return t('automations.sendTo.sendToAssignedTrainer');
}

/**
 * Send To Summary Component
 * Displays a readonly summary of who receives the automation
 */
export const SendToSummary: FC<{
  automation: AutomationItem;
  placeholder?: string;
}> = ({ automation, placeholder }) => {
  const { t } = useTranslation();

  // Fetch trainers for display
  const { data: trainers = [] } = useQuery({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  // Convert sendToIds to numbers for consistent comparison
  const sendToIds = (automation.sendToIds || []).map((id) =>
    typeof id === 'string' ? parseInt(id, 10) : id
  );
  const placeholderText = placeholder || t('automations.sendTo.sendToClient');

  // Empty sendToIds means send to client
  if (sendToIds.length === 0) {
    return <>{placeholderText}</>;
  }

  // Build display text
  const parts: string[] = [];

  // Check for special "Assigned Trainer" option (value: 0)
  if (sendToIds.includes(0)) {
    parts.push(getSpecialOptionLabel(automation, t));
  }

  // Get trainer names for other IDs
  const trainerIds = sendToIds.filter((id) => id !== 0);
  if (trainerIds.length > 0) {
    const trainerNames = trainerIds
      .map((id) => {
        const trainer = trainers.find((t) => t.id === id);
        return getUserDisplayName(trainer);
      })
      .filter(Boolean);

    if (trainerNames.length > 0) {
      parts.push(...trainerNames);
    }
  }

  if (parts.length === 0) {
    return <i>{t('automations.sendTo.unableToFindStaff')}</i>;
  }

  return <>{parts.join(', ')}</>;
};
